import { useState } from 'react';
import { useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PublicNewsletterInfo } from '@kontor/shared';
import { api, ApiError } from '../../api';
import './marketing.css';

const failText = (e: unknown) => (e instanceof ApiError ? (e.status === 429 ? 'Zu viele Versuche. Bitte warten Sie einige Minuten.' : e.message) : 'Das hat nicht geklappt. Bitte versuchen Sie es noch einmal.');

function Frame({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="center-screen">
      <div className="card narrow">
        <h1>{title}</h1>
        {children}
      </div>
    </div>
  );
}

const Invalid = () => (
  <Frame title="Link nicht gültig">
    <p className="muted">Dieser Link ist ungültig oder wurde schon benutzt. Wenn Sie sich anmelden oder abmelden möchten, nutzen Sie bitte den Link aus der jüngsten E-Mail.</p>
  </Frame>
);

/**
 * Öffentliche Seite zum Bestätigen der Newsletter-Anmeldung (Double-Opt-In). Erst der Klick auf die Schaltfläche bestätigt, nicht schon das Öffnen des Links, damit Virenscanner und
 * Linkvorschauen der Mailprogramme keine Anmeldung auslösen.
 */
export function NewsletterConfirmPage() {
  const { token = '' } = useParams();
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const info = useQuery({ queryKey: ['newsletter-confirm', token], queryFn: async () => (await api<{ data: PublicNewsletterInfo }>('GET', `/public/newsletter/confirm/${token}`)).data, retry: false });
  const confirm = useMutation({
    mutationFn: async () => (await api<{ data: PublicNewsletterInfo }>('POST', `/public/newsletter/confirm/${token}`)).data,
    onSuccess: (d) => client.setQueryData(['newsletter-confirm', token], d),
    onError: (e) => setError(failText(e)),
  });
  if (info.isLoading) return <div className="center-screen">Wird geladen …</div>;
  if (info.isError || !info.data) return <Invalid />;
  const { company, status } = info.data;
  if (status === 'subscribed') {
    return (
      <Frame title="Anmeldung bestätigt">
        <p>Vielen Dank! Sie erhalten ab jetzt den Newsletter von <strong>{company}</strong>. Abmelden können Sie sich jederzeit über den Link am Ende jeder Mail.</p>
      </Frame>
    );
  }
  if (status === 'unsubscribed') {
    return <Frame title="Abgemeldet"><p className="muted">Sie sind vom Newsletter von {company} abgemeldet.</p></Frame>;
  }
  return (
    <Frame title="Newsletter-Anmeldung bestätigen">
      <p>Möchten Sie den Newsletter von <strong>{company}</strong> erhalten? Mit dem Klick bestätigen Sie Ihre E-Mail-Adresse und stimmen dem Empfang zu. Sie können sich jederzeit wieder abmelden.</p>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <button type="button" className="btn btn--primary" onClick={() => { setError(null); confirm.mutate(); }} disabled={confirm.isPending}>{confirm.isPending ? 'Wird bestätigt …' : 'Anmeldung bestätigen'}</button>
    </Frame>
  );
}

/** Öffentliche Seite zum Abmelden über den Link in einer Kampagne. Ein Klick genügt, ein Grund ist freiwillig. */
export function NewsletterUnsubscribePage() {
  const { token = '' } = useParams();
  const client = useQueryClient();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const info = useQuery({ queryKey: ['newsletter-unsubscribe', token], queryFn: async () => (await api<{ data: PublicNewsletterInfo }>('GET', `/public/newsletter/unsubscribe/${token}`)).data, retry: false });
  const off = useMutation({
    mutationFn: async () => (await api<{ data: PublicNewsletterInfo }>('POST', `/public/newsletter/unsubscribe/${token}`, { reason: reason.trim() || undefined })).data,
    onSuccess: (d) => client.setQueryData(['newsletter-unsubscribe', token], d),
    onError: (e) => setError(failText(e)),
  });
  if (info.isLoading) return <div className="center-screen">Wird geladen …</div>;
  if (info.isError || !info.data) return <Invalid />;
  const { company, status } = info.data;
  if (status === 'unsubscribed') {
    return (
      <Frame title="Sie sind abgemeldet">
        <p>Sie erhalten keinen Newsletter mehr von <strong>{company}</strong>. Das tut uns leid, Sie können sich jederzeit wieder anmelden.</p>
      </Frame>
    );
  }
  return (
    <Frame title="Vom Newsletter abmelden">
      <p>Möchten Sie keine E-Mails mehr von <strong>{company}</strong> erhalten?</p>
      <label className="field">
        <span>Grund (freiwillig)</span>
        <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={200} />
      </label>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <button type="button" className="btn btn--primary" onClick={() => { setError(null); off.mutate(); }} disabled={off.isPending}>{off.isPending ? 'Wird abgemeldet …' : 'Abmelden'}</button>
    </Frame>
  );
}
